import { anchorPosition } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { createEntity, type EntityId } from '@/domain/model';
import { hitTest } from '@/engine';

import { type PointerInput, type Tool, type ToolContext, type ToolResponse } from './Tool';

/**
 * The annotation tool.
 *
 * Clicking places an empty label and asks for it to be edited. Clicking an existing label
 * edits that one instead, so the tool is "the label tool" rather than "the make-a-label
 * tool" — a user who has just written one and wants to fix a typo should not have to change
 * tools first.
 *
 * ## Where the typing happens
 *
 * Not here. The tool reports *which* label wants editing and the shell puts a real text
 * field over it. That division is what buys IME, and IME is not optional: a Vietnamese,
 * Japanese or Korean user composes through a system input method, and a canvas that reads
 * `keydown` cannot see a composition at all. The editor is a `<textarea>`, so composition,
 * selection, autocorrect and the platform's own editing shortcuts all work because they were
 * never reimplemented.
 */

export interface TextEditRequest {
  readonly id: EntityId;
  /** Where the label sits, in world coordinates. */
  readonly at: Vec2;
}

export class TextTool implements Tool {
  readonly id = 'text';
  readonly label = 'Chữ';
  readonly shortcut = 't';
  readonly group = 'Vẽ';

  /** Set when a click asks for an editor. Read and cleared by the controller. */
  pendingEdit: TextEditRequest | null = null;

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    // A label already here is edited rather than having a second one stacked on it.
    const existing = hitTest(context.scene, input.world, context.tolerance, {
      filter: (_id, kind) => kind === 'text',
    });
    if (existing !== null && context.document.entities.get(existing.id)?.source.op === 'text') {
      this.pendingEdit = { id: existing.id, at: input.world };
      return { selection: [existing.id] };
    }

    /*
     * Anything else under the cursor becomes the label's anchor, and the label then *follows
     * it*. This is the difference between "a note near the point" and "this point is called
     * A": drag the point and the note stays behind, which is exactly the disappointment a
     * user reports as the label being broken.
     *
     * The offset is measured from the anchor's own position rather than from the click, so a
     * label placed on a vertex sits where it was dropped and keeps that relationship.
     */
    const host = hitTest(context.scene, input.world, context.tolerance, {
      filter: (_id, kind) => kind !== 'text',
    });

    const anchor = host === null ? null : host.id;
    const anchorAt = anchor === null ? null : anchorPosition(context.scene.values.get(anchor));
    const offset = anchorAt === null ? input.world : vec.subtract(input.world, anchorAt);

    const id = context.nextId('txt');
    this.pendingEdit = { id, at: input.world };

    return {
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(
            id,
            { op: 'text', anchor, offset, content: '', latex: false },
            context.style
          ),
        },
      ],
      selection: [id],
      label: 'Add label',
      sealHistory: true,
    };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    const label = hitTest(context.scene, input.world, context.tolerance, {
      filter: (_id, kind) => kind === 'text',
    });
    if (label !== null) return { cursor: 'pointer' };

    // `copy` over an object says the label will attach to it, which is a different outcome
    // from dropping one on empty canvas and worth showing before the click.
    const host = hitTest(context.scene, input.world, context.tolerance, {
      filter: (_id, kind) => kind !== 'text',
    });
    return { cursor: host === null ? 'text' : 'copy' };
  }

  cancel(): ToolResponse | null {
    this.pendingEdit = null;
    return null;
  }

  reset(): void {
    this.pendingEdit = null;
  }
}
